import isEmpty from 'lodash.isempty'
import * as Yup from 'yup'
import {
  CheckSpecialValidation,
  ShowInvalidErrorMessage,
} from './InputValidationHelper'

export const parseFormikValues = (values, questionList) => {
  let keyNames = Object.keys(values)
  let answers = []
  keyNames.forEach(function (key) {
    const question = {
      questionId: key,
      answers: [],
    }
    //single selection
    if (typeof values[key] === 'string') {
      const answerResponse = constructAnswerResponse(
        key,
        values[key],
        questionList,
        values
      )
      if (answerResponse) {
        question.answers.push(answerResponse)
      }
    }
    //muti-selection, selection is an array
    if (Array.isArray(values[key])) {
      values[key].forEach(function (selected) {
        const answer = {
          answerId: selected,
        }
        question.answers.push(answer)
      })
    }
    answers.push(question)
  })
  return answers
}

const constructAnswerResponse = (
  questionId,
  answerValue,
  questionList,
  values
) => {
  const foundQuestion = questionList?.find(e => e.id === questionId)

  if (!foundQuestion) {
    return
  }

  const textboxOption = foundQuestion?.answers?.find(
    e =>
      e.answerType === 'text' ||
      e.answerType === 'textarea' ||
      e.answerType === 'date'
  )

  // getting the entire list of answers that are off type radio
  const radioButtonList = foundQuestion?.answers.filter(
    e => e.answerType === 'radio'
  )

  // condition that checks if the textBox option exists and the radio button list isn't an empty array
  // to determine if an answer is a write in option
  const answerIsOfTypeWriteInOption = textboxOption && radioButtonList?.length

  if (answerIsOfTypeWriteInOption) {
    // grabs the last item in the radio button list
    const radioButton = radioButtonList[radioButtonList.length - 1]
    // checks if the radio button id matches one of the items in the values array
    // if so returns the id along with the answerValue
    if (radioButton?.id == values[questionId]) {
      return {
        answerId: textboxOption.id,
        answerValue: values[textboxOption.id],
      }
    }
    // else returns only the answerId
    return {
      answerId: values[questionId],
    }
  }

  if (textboxOption) {
    return {
      answerId: textboxOption.id,
      answerValue: answerValue,
    }
  }

  if (!textboxOption && !isNaN(answerValue)) {
    return {
      answerId: answerValue,
    }
  }

  return null
}

export const parseSelectionAnswers = (values, questionList) => {
  let keyNames = Object.keys(values)
  let answers = []
  keyNames.forEach(function (key) {
    if (Number.isInteger(parseInt(key))) {
      const currentQuestion = questionList?.find(e => e.id === key)
      const question = {
        questionId: key,
        answers: [],
      }
      if (currentQuestion) {
        // maintenance package flow
        //single selection non-text input
        if (
          typeof values[key] === 'string' &&
          currentQuestion?.answers[0]?.answerType !== 'text' &&
          currentQuestion?.answers[0]?.answerType !== 'textarea'
        ) {
          question.answers.push({ answerId: values[key] })
        }
        // text or textarea input
        if (
          (typeof values[key] === 'string' &&
            currentQuestion?.answers[0]?.answerType === 'text') ||
          currentQuestion?.answers[0]?.answerType === 'textarea'
        ) {
          question.answers.push({
            answerId: currentQuestion?.id,
            answerValue: values[key],
          })
        }
      } else {
        //single selection
        if (typeof values[key] === 'string') {
          question.answers.push({ answerId: values[key] })
        }
      }
      //muti-selection, selection is an array
      if (Array.isArray(values[key])) {
        values[key].forEach(function (selected) {
          const answer = {
            answerId: selected,
          }
          question.answers.push(answer)
        })
      }
      answers.push(question)
    }
  })
  return answers
}

export const MergeAllQuestions = questionsWithStep => {
  let questions = []
  questionsWithStep.forEach(e => {
    questions = questions.concat(e.questions)
  })
  return questions
}

const createIsHide = ({ id, schemaObject, yupTypeObject, errorMessage }) => {
  const isHideID = `${id}-isHide`
  schemaObject[isHideID] = Yup.boolean()
  schemaObject[id] = yupTypeObject.when(isHideID, {
    is: false,
    then: yupTypeObject.required(errorMessage),
    otherwise: yupTypeObject.optional(),
  })
}

const getStrategyBuilderType = ({ id, answerType, text, errorMessage }) => {
  const getYupType = type => {
    switch (type) {
      case 'PercentInput':
        return Yup.string()
    }
  }
  const containsDesiredText = text.toLowerCase().includes('desired')
  let schemaObject = {}
  const yupTypeObject = getYupType(answerType)
  if (containsDesiredText) {
    createIsHide({
      id,
      schemaObject,
      yupTypeObject,
      errorMessage,
    })
  }
  return schemaObject
}

// Dynamic validation for formik
export const getDataSchema = ({
  questions,
  type = 'support-package',
  errorMessage = 'Please fill in your response',
  values,
}) => {
  const isSupportPackage = type === 'support-package'

  const getSpecialType = (specialTypeMessage, yupObj, question) => {
    return yupObj.test(
      question.validation.specialFormat,
      specialTypeMessage,
      value => CheckSpecialValidation(question.validation.specialFormat, value)
    )
  }

  const getYupType = (type, answers) => {
    switch (type) {
      case 'radio':
      case 'checkbox':
      case 'dropdown':
        return Yup.string().oneOf(answers.map(answer => answer.id))
      case 'text':
      case 'textarea':
        return Yup.string()
      case 'number':
        return Yup.number()
      case 'date':
        return Yup.date()
      case 'file':
        return Yup.mixed()
      case 'boolean':
        return Yup.boolean()
      default:
        return null
    }
  }

  if (isEmpty(questions)) return
  const answersFormatted = questions.map(question => {
    const { requiredNumberOfAnswers, answers, hideTrigger } = question
    const required = requiredNumberOfAnswers > 0
    const answer = answers[0]
    const { id, questionId, answerType } = answer
    const ID = isSupportPackage ? questionId : id
    const specialTypeMessage = ShowInvalidErrorMessage(question, values)
    let schemaObject = {}
    let yupTypeObject = getYupType(answerType, answers)

    if (specialTypeMessage) {
      yupTypeObject = getSpecialType(
        specialTypeMessage,
        yupTypeObject,
        question
      )
    }

    if (yupTypeObject) {
      if (!isEmpty(hideTrigger)) {
        createIsHide({ id: ID, schemaObject, yupTypeObject, errorMessage })
      } else {
        schemaObject[ID] = yupTypeObject.required(
          required ? errorMessage : false
        )
      }
      return isSupportPackage ? schemaObject : {}
    } else {
      return getStrategyBuilderType({ ...answer, required, errorMessage })
    }
  })
  const finalObj = {
    ...Object.assign({}, ...[].concat.apply([], answersFormatted)),
  }
  const schema = Yup.object().shape(finalObj)
  return schema
}

export const cleanUpIsHide = values => {
  if (!isEmpty(values)) {
    Object.keys(values).forEach(function (key) {
      if (key.includes('-isHide')) {
        delete values[key]
      }
    })
  }
}

export const verifyIfInputIsDisabled = ({
  relatedQuestions,
  question,
  values,
}) => {
  if (Object.prototype.hasOwnProperty.call(relatedQuestions, question.id)) {
    const relatedQuestionIds = relatedQuestions[question.id]
    if (relatedQuestionIds[0] !== '') {
      const relatedQuestionsAnswers = relatedQuestionIds.map(
        questionId => values[questionId]
      )
      const isDisabled = relatedQuestionsAnswers.some(questionAnswer =>
        isEmpty(questionAnswer)
      )
      return isDisabled
    }
    return false
  }
  return false
}
